import { supabase } from '../config/supabase.js';
import { CRM_USER_COLUMNS } from './crmService.js';

// Users only ever type their username - this is an internal detail
// Supabase Auth requires (it needs an email-shaped identifier), never
// shown or typed by anyone.
const EMAIL_DOMAIN = 'staticcrm.com';

function usernameToEmail(username) {
    const local = username.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    return `${local}@${EMAIL_DOMAIN}`;
}

// Authentication Service
class AuthService {
    constructor() {
        this.USER_KEY = 'crm_user';
    }

    // Get Supabase client
    getSupabase() {
        return supabase;
    }

    // Login
    async login(username, password) {
        try {
            const supabase = this.getSupabase();
            const email = usernameToEmail(username);

            const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (authError || !authData?.user) {
                console.error('Supabase auth error:', authError);
                throw new Error(authError?.message || 'Invalid username or password');
            }

            // Load the profile row (role, team_id, name, etc.) linked to this Auth account.
            // Credentials are verified by Supabase Auth above; this row is only metadata.
            const { data: profile, error: profileError } = await supabase
                .from('crm_users')
                .select(CRM_USER_COLUMNS)
                .eq('auth_user_id', authData.user.id)
                .single();

            if (profileError || !profile) {
                await supabase.auth.signOut();
                throw new Error('Invalid username or password');
            }

            // Update last_login timestamp
            await supabase
                .from('crm_users')
                .update({ last_login: new Date().toISOString() })
                .eq('username', profile.username);

            // Store locally (Supabase's own client already persists the real
            // session/token separately - this is just a cached profile so the
            // rest of the app can keep reading the same shape as before)
            localStorage.setItem(this.USER_KEY, JSON.stringify(profile));

            return {
                success: true,
                user: profile
            };
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    // Logout
    async logout() {
        try {
            const supabase = this.getSupabase();
            await supabase.auth.signOut();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem(this.USER_KEY);
        }
    }

    // Check if logged in
    isLoggedIn() {
        return !!localStorage.getItem(this.USER_KEY);
    }

    // Get current user
    getCurrentUser() {
        const userStr = localStorage.getItem(this.USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
    }

    // Re-fetch the profile row fresh from the DB - a locally cached copy
    // from login time can go stale the moment an admin edits this user's
    // role or team, so every session restore needs the current row, not
    // whatever was true when they last typed a password.
    async refreshProfile() {
        try {
            const supabase = this.getSupabase();
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return null;

            const { data: profile, error } = await supabase
                .from('crm_users')
                .select(CRM_USER_COLUMNS)
                .eq('auth_user_id', session.user.id)
                .single();

            if (error || !profile) return null;

            localStorage.setItem(this.USER_KEY, JSON.stringify(profile));
            return profile;
        } catch (error) {
            console.error('Profile refresh error:', error);
            return null;
        }
    }
}

export const authService = new AuthService();
